import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Button,
  Card,
  Input,
  Option,
  Select,
  Stack,
  Tab,
  TabList,
  TabPanel,
  Tabs,
  Typography,
} from '@mui/joy';
import { CalendarDays, RotateCcw, Search, SlidersHorizontal } from 'lucide-react';
import { fetchVisibleCampagnesRequest } from '../../../redux/actions/campagnesActions';
import { getKpiDashboardsMetadataRequest } from '../../../redux/actions/kpiActions';
import DynamicKpiSection from '../../adminrh/components/DynamicKpiSection';
import DynamicReportSection from '../../adminrh/components/DynamicReportSection';

const safeArray = (value) => (Array.isArray(value) ? value : []);

const normalizeFormationRole = (value) => {
  const raw = String(value || '')
    .trim()
    .toUpperCase()
    .replace(/-/g, '_')
    .replace(/\s+/g, '_');

  if (
    raw.includes('RESPONSABLE_DE_FORMATION')
    || raw.includes('RESPONSABLE_FORMATION')
    || raw === 'RF'
    || raw.endsWith('_RF')
    || raw === 'ROLE_RF'
  ) {
    return 'RESPONSABLE_FORMATION';
  }
  if (raw.includes('MANAGER')) return 'MANAGER';
  if (
    raw.includes('APPRENANT')
    || raw.includes('COLLABORATEUR')
    || raw.includes('COLLABORATOR')
    || raw === 'ROLE_USER'
    || raw === 'USER'
  ) {
    return 'COLLABORATOR';
  }
  return 'COLLABORATOR';
};

const mapSelectedRoleToKpiRole = (selectedRole) => normalizeFormationRole(
  [
    selectedRole?.code,
    selectedRole?.name,
    selectedRole?.label,
    selectedRole?.roleName,
    selectedRole?.authority,
  ].filter(Boolean).join(' ')
);

const emptyFilters = {
  campaignId: null,
  dateFrom: '',
  dateTo: '',
  globalSearch: '',
};

const shellCardSx = {
  borderRadius: '14px',
  borderColor: '#e2e8f0',
  boxShadow: '0 8px 20px rgba(15,23,42,0.06)',
  backgroundColor: '#fff',
};

export default function FormationDynamicDashboardPage() {
  const dispatch = useDispatch();
  const selectedRole = useSelector((state) => state.role?.selectedRole);
  const { visibleList, loading: campagnesLoading } = useSelector((state) => state.campagnes || {});
  const { metadata } = useSelector((state) => state.kpi || {});

  const [filters, setFilters] = useState(emptyFilters);
  const [activeTab, setActiveTab] = useState(0);

  const roleKey = useMemo(() => mapSelectedRoleToKpiRole(selectedRole), [selectedRole]);
  const dashboards = useMemo(() => safeArray(metadata?.dashboards), [metadata]);
  const dashboardKey = dashboards[0]?.key || '';
  const canExport = dashboardKey && dashboardKey !== 'FORMATION_APPRENANT';

  useEffect(() => {
    dispatch(fetchVisibleCampagnesRequest());
  }, [dispatch]);

  useEffect(() => {
    dispatch(getKpiDashboardsMetadataRequest('FORMATION', roleKey));
  }, [dispatch, roleKey]);

  useEffect(() => {
    if (!canExport && activeTab === 1) {
      setActiveTab(0);
    }
  }, [activeTab, canExport]);

  const runtimeFilters = useMemo(() => ({
    roleKey,
    campaignId: filters.campaignId || null,
    dateFrom: filters.dateFrom || null,
    dateTo: filters.dateTo || null,
    globalSearch: filters.globalSearch || null,
  }), [filters, roleKey]);

  return (
    <Box
      sx={{
        width: '100%',
        minHeight: '100vh',
        boxSizing: 'border-box',
        p: '24px 18px 18px',
        background: '#fff',
        overflowX: 'hidden',
      }}
    >
      <Stack spacing={2.5}>
        <Box sx={{ px: 0, mb: 0.5 }}>
          <Typography
            level="h2"
            sx={{
              color: '#204385',
              fontSize: 20,
              fontWeight: 800,
              lineHeight: 1.2,
            }}
          >
            Dashboard Formation
          </Typography>

        </Box>

        <Card
          variant="outlined"
          sx={{
            ...shellCardSx,
            p: 1.5,
            overflowX: 'auto',
            overflowY: 'hidden',
            '&::-webkit-scrollbar': {
              height: 6,
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: '#cbd5e1',
              borderRadius: 999,
            },
          }}
        >
          <Stack
            direction="row"
            spacing={1.25}
            useFlexGap
            sx={{
              alignItems: 'center',
              flexWrap: 'nowrap',
              minWidth: 'max-content',
            }}
          >
            <Select
              value={filters.campaignId}
              onChange={(event, value) => setFilters((prev) => ({ ...prev, campaignId: value }))}
              placeholder={campagnesLoading ? 'Chargement campagnes...' : 'Campagne'}
              startDecorator={<SlidersHorizontal size={16} />}
              sx={{
                width: 260,
                minWidth: 260,
                backgroundColor: '#fff',
                '--Select-radius': '10px',
                '--Select-minHeight': '34px',
                fontSize: 13,
              }}
            >
              <Option value={null}>Toutes les campagnes</Option>
              {safeArray(visibleList).map((campagne) => (
                <Option key={campagne.id} value={campagne.id}>
                  {campagne.nom || campagne.name || campagne.reference}
                </Option>
              ))}
            </Select>

            <Input
              type="date"
              value={filters.dateFrom}
              onChange={(event) => setFilters((prev) => ({ ...prev, dateFrom: event.target.value }))}
              startDecorator={<CalendarDays size={16} />}
              sx={{
                width: 170,
                minWidth: 170,
                backgroundColor: '#fff',
                '--Input-radius': '10px',
                '--Input-minHeight': '34px',
                fontSize: 13,
              }}
            />

            <Input
              type="date"
              value={filters.dateTo}
              onChange={(event) => setFilters((prev) => ({ ...prev, dateTo: event.target.value }))}
              startDecorator={<CalendarDays size={16} />}
              sx={{
                width: 170,
                minWidth: 170,
                backgroundColor: '#fff',
                '--Input-radius': '10px',
                '--Input-minHeight': '34px',
                fontSize: 13,
              }}
            />

            <Input
              value={filters.globalSearch}
              onChange={(event) => setFilters((prev) => ({ ...prev, globalSearch: event.target.value }))}
              startDecorator={<Search size={16} />}
              placeholder="Recherche globale"
              sx={{
                width: 360,
                minWidth: 360,
                backgroundColor: '#fff',
                '--Input-radius': '10px',
                '--Input-minHeight': '34px',
                fontSize: 13,
              }}
            />

            <Button
              variant="soft"
              color="neutral"
              startDecorator={<RotateCcw size={16} />}
              onClick={() => setFilters(emptyFilters)}
              sx={{
                minWidth: 120,
                height: 34,
                borderRadius: '10px',
                fontSize: 13,
                fontWeight: 700,
                color: '#111827',
              }}
            >
              Reinitialiser
            </Button>
          </Stack>
        </Card>

        <Tabs value={activeTab} onChange={(event, value) => setActiveTab(value)} sx={{ '--Tabs-gap': '12px' }}>
          <TabList
            sx={{
              p: 0.75,
              borderRadius: '10px',
              backgroundColor: '#fff',
              border: '1px solid #e5e7eb',
              boxShadow: '0 1px 2px rgba(16,24,40,.06)',
              gap: 0.75,
            }}
          >
            <Tab
              value={0}
              sx={{
                borderRadius: '8px',
                fontWeight: 700,
                fontSize: 13,
                color: '#475569',
                minHeight: 32,
                px: 1.75,
                '&[aria-selected="true"]': {
                  backgroundColor: '#204385',
                  color: '#fff',
                },
              }}
            >
              KPIs
            </Tab>
            {canExport && (
              <Tab
                value={1}
                sx={{
                  borderRadius: '8px',
                  fontWeight: 700,
                  fontSize: 13,
                  color: '#475569',
                  minHeight: 32,
                  px: 1.75,
                  '&[aria-selected="true"]': {
                    backgroundColor: '#204385',
                    color: '#fff',
                  },
                }}
              >
                Etats Excel
              </Tab>
            )}
          </TabList>

          <TabPanel value={0} sx={{ px: 0, pt: 2 }}>
            {dashboardKey ? (
              <DynamicKpiSection dashboardKey={dashboardKey} runtimeFilters={runtimeFilters} />
            ) : (
              <Card variant="outlined" sx={{ ...shellCardSx, p: 2.5 }}>
                <Typography level="body-sm" sx={{ color: '#64748b' }}>
                  Aucun dashboard Formation disponible pour ce role.
                </Typography>
              </Card>
            )}
          </TabPanel>

          {canExport && (
            <TabPanel value={1} sx={{ px: 0, pt: 2 }}>
              <DynamicReportSection dashboardKey={dashboardKey} runtimeFilters={runtimeFilters} />
            </TabPanel>
          )}
        </Tabs>
      </Stack>
    </Box>
  );
}
